import React, { useEffect, useMemo, useState } from 'react';
import {
  HeaderRow,
  Subtle,
  InlineLinkBox,
} from './DevicesConfiguredListStyles';
import { ActionsCell, IconButton } from '../../../../components/Table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  TextField,
  MenuItem,
} from '@mui/material';
import { Station } from '../../../../types/stations.types';
import { getStations } from '../../../../services/stationsService';
import { VacuumOption } from '../../../../types/vacuum.types';
import { LaundryMachineOption } from '../../../../types/laundry.types';
import { WashingVehicle } from '../../../../types/carWashing.types';
import {
  deleteVacuumCleaner,
  getAllVacuumCleaners,
  getVacuumCleanersByStation,
  linkDeviceToVacuumCleaner,
} from '../../../../services/vacuumService';
import {
  deleteCarWashing,
  getAllCarWashings,
  getCarWashings,
} from '../../../../services/carWashingsService';
import {
  deleteLaundryMachine,
  getAllLaundryMachines,
  getLaundryMachinesByStation,
  linkDeviceToLaundryMachine,
} from '../../../../services/laundryService';
import Button from '../../../../components/Button';

type ServiceFilter = 'ALL' | 'VACUUM' | 'WASH' | 'LAUNDRY';

type NormalizedItem =
  | {
      kind: 'VACUUM';
      id: string;
      name: string;
      deviceId: string;
      stationCode: string;
      serialNumber?: string;
    }
  | {
      kind: 'WASH';
      id: string;
      name: string; // washingMachine
      stationCode: string;
    }
  | {
      kind: 'LAUNDRY';
      id: string;
      name: string;
      deviceId: string;
      stationCode: string;
      serialNumber?: string;
    };

const isEmptySerial = (s?: string | null) => !s || !String(s).trim();

const DevicesConfiguredList: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [stationCode, setStationCode] = useState<string>('ALL');
  const [service, setService] = useState<ServiceFilter>('ALL');

  const [items, setItems] = useState<NormalizedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // controle do "vincular" inline
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [serialToLink, setSerialToLink] = useState<string>('');

  const stationOptions = useMemo(() => {
    return [
      { code: 'ALL', name: 'Todos' },
      ...stations.map((s) => ({ code: s.code, name: s.name })),
    ];
  }, [stations]);

  const normalize = (
    vac: VacuumOption[] = [],
    wash: WashingVehicle[] = [],
    lau: LaundryMachineOption[] = []
  ): NormalizedItem[] => {
    const nVac: NormalizedItem[] = vac.map((v) => ({
      kind: 'VACUUM',
      id: v.id,
      name: v.name,
      deviceId: v.deviceId,
      stationCode: v.station,
      serialNumber: v.serialNumber || undefined,
    }));

    const nWash: NormalizedItem[] = wash.map((w) => ({
      kind: 'WASH',
      id: w.id,
      name: w.washingMachine,
      stationCode: w.station,
    }));

    const nLau: NormalizedItem[] = lau.map((l) => ({
      kind: 'LAUNDRY',
      id: l.id,
      name: l.name,
      deviceId: l.deviceId,
      stationCode: Array.isArray(l.station)
        ? l.station[0]?.code || ''
        : l.station?.code || '',
      serialNumber: l.serialNumber,
    }));

    return [...nVac, ...nWash, ...nLau];
  };

  const loadStations = async () => {
    try {
      const res = await getStations();
      setStations(res);
    } catch (e) {
      console.error(e);
      // não travo a tela toda por station, mas registro
    }
  };

  const loadDevices = async () => {
    try {
      setLoading(true);
      setErrorMsg('');

      const isAllStation = stationCode === 'ALL';
      const isAllService = service === 'ALL';

      // Helpers para chamadas (descomenta quando plugar services)
      const fetchVacuum = async (): Promise<VacuumOption[]> => {
        if (isAllStation) return await getAllVacuumCleaners();
        return await getVacuumCleanersByStation(stationCode);
      };

      const fetchWash = async (): Promise<WashingVehicle[]> => {
        if (isAllStation) return await getAllCarWashings();
        return await getCarWashings(stationCode);
      };

      const fetchLaundry = async (): Promise<LaundryMachineOption[]> => {
        if (isAllStation) return await getAllLaundryMachines();
        return await getLaundryMachinesByStation(stationCode);
      };

      if (isAllService) {
        const [vac, wash, lau] = await Promise.all([
          fetchVacuum(),
          fetchWash(),
          fetchLaundry(),
        ]);
        setItems(normalize(vac, wash, lau));
        return;
      }

      if (service === 'VACUUM') {
        const vac = await fetchVacuum();
        setItems(normalize(vac, [], []));
        return;
      }

      if (service === 'WASH') {
        const wash = await fetchWash();
        setItems(normalize([], wash, []));
        return;
      }

      const lau = await fetchLaundry();
      setItems(normalize([], [], lau));
    } catch (e) {
      console.error(e);
      setErrorMsg('Erro ao buscar devices configurados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadStations();
    void loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationCode, service]);

  const labelKind = (k: NormalizedItem['kind']) => {
    if (k === 'VACUUM') return 'Aspirador';
    if (k === 'WASH') return 'Lavação';
    return 'Lavanderia';
  };

  const handleDelete = async (item: NormalizedItem) => {
    const ok = window.confirm(
      `Tem certeza que deseja remover "${item.name}"?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      if (item.kind === 'VACUUM') await deleteVacuumCleaner(item.deviceId);
      if (item.kind === 'WASH') await deleteCarWashing(item.id);
      if (item.kind === 'LAUNDRY') await deleteLaundryMachine(item.id);

      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      console.error(e);
      alert('Erro ao remover.');
    } finally {
      setLoading(false);
    }
  };

  const openLink = (item: NormalizedItem) => {
    setLinkingId(item.id);
    setSerialToLink('');
  };

  const closeLink = () => {
    setLinkingId(null);
    setSerialToLink('');
  };

  const handleUnlink = async (item: NormalizedItem) => {
    const ok = window.confirm(
      `Tem certeza que deseja DESVINCULAR o serial do "${item.name}"?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      if (item.kind === 'VACUUM') {
        await linkDeviceToVacuumCleaner(item.id);
      }
      if (item.kind === 'LAUNDRY') {
        await linkDeviceToLaundryMachine(item.id);
      }

      setItems((prev) =>
        prev.map((x) => (x.id === item.id ? { ...x, serialNumber: '' } as any : x))
      );
    } catch (e) {
      console.error(e);
      alert('Erro ao desvincular.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLink = async (item: NormalizedItem) => {
    const serial = serialToLink.trim();
    if (!serial) {
      alert('Informe o serialNumber para vincular.');
      return;
    }

    try {
      setLoading(true);

      if (item.kind === 'VACUUM') {
        await linkDeviceToVacuumCleaner(item.id, serial);
      }
      if (item.kind === 'LAUNDRY') {
        await linkDeviceToLaundryMachine(item.id, serial);
      }

      setItems((prev) =>
        prev.map((x) => (x.id === item.id ? ({ ...x, serialNumber: serial } as any) : x))
      );

      closeLink();
    } catch (e) {
      console.error(e);
      alert('Erro ao vincular.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HeaderRow>
        <Subtle>
          {loading ? 'Carregando...' : `Mostrando ${items.length} device(s)`}
        </Subtle>
        <div style={{ display: 'flex' }}>
          <Button
            title={loading ? 'Atualizando...' : 'Atualizar'}
            onClick={loadDevices}
            disabled={loading}
          />
        </div>
      </HeaderRow>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Posto"
          size="small"
          variant="outlined"
          value={stationCode}
          onChange={(e) => setStationCode(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {stationOptions.map((s) => (
            <MenuItem key={s.code} value={s.code}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Serviço"
          size="small"
          variant="outlined"
          value={service}
          onChange={(e) => setService(e.target.value as ServiceFilter)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="ALL">Todos</MenuItem>
          <MenuItem value="VACUUM">Aspirador</MenuItem>
          <MenuItem value="WASH">Lavação</MenuItem>
          <MenuItem value="LAUNDRY">Lavanderia</MenuItem>
        </TextField>
      </Stack>

      {errorMsg && <Subtle style={{ color: '#d00' }}>{errorMsg}</Subtle>}

      <TableContainer component={Paper} sx={{ overflowX: 'auto', marginTop: '16px' }}>
        <Table sx={{ minWidth: 650 }} aria-label="configured devices table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Nome</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Tipo</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Posto</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Serial number</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum device configurado encontrado.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const canLink = item.kind === 'VACUUM' || item.kind === 'LAUNDRY';
                const hasSerial =
                  'serialNumber' in item && !isEmptySerial(item.serialNumber);

                return (
                  <React.Fragment key={`${item.kind}-${item.id}`}>
                    <TableRow hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                        {item.name}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {labelKind(item.kind)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {item.stationCode || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {canLink ? (hasSerial ? (item as any).serialNumber : 'N/A') : '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                        <ActionsCell style={{ justifyContent: 'flex-end' }}>
                          {canLink && !hasSerial && (
                            <IconButton
                              type="button"
                              onClick={() => openLink(item)}
                              title="Vincular serial number"
                            >
                              Vincular
                            </IconButton>
                          )}

                          {canLink && hasSerial && (
                            <IconButton
                              type="button"
                              onClick={() => handleUnlink(item)}
                              title="Desvincular serial number"
                            >
                              Desvincular
                            </IconButton>
                          )}

                          <IconButton
                            type="button"
                            onClick={() => handleDelete(item)}
                            $danger
                            title="Deletar"
                          >
                            Deletar
                          </IconButton>
                        </ActionsCell>
                      </TableCell>
                    </TableRow>

                    {linkingId === item.id && canLink && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ p: 0, borderBottom: 'none' }}>
                          <InlineLinkBox>
                            <TextField
                              size="small"
                              variant="outlined"
                              value={serialToLink}
                              onChange={(e) => setSerialToLink(e.target.value)}
                              placeholder="Digite o serialNumber (ex: ABC123)"
                              sx={{ minWidth: 260 }}
                            />
                            <ActionsCell>
                              <IconButton
                                type="button"
                                onClick={() => handleConfirmLink(item)}
                              >
                                Salvar
                              </IconButton>
                              <IconButton type="button" $danger onClick={closeLink}>
                                Cancelar
                              </IconButton>
                            </ActionsCell>
                          </InlineLinkBox>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default DevicesConfiguredList;
